/* eslint-disable sort-keys */
import 'whatwg-fetch';
import isEmpty from 'lodash/isEmpty';
import { call, put } from 'redux-saga/effects';

import { logoutRequest } from '../redux/Main/actions';
import AsyncStorage from './AsyncStorage';
import {
  ANONYMOUS_SPLIT_ID_HEADER_TITLE,
  ANONYMOUS_SPLIT_ID_STORAGE_KEY,
  AUTH_TOKEN_SESSION_KEY,
  CONTENT_TYPES,
  DEVICE_TOKENS_HEADER_TITLE,
  DEVICE_TOKENS_STORAGE_KEY,
  SESSION_HEADERS_STORAGE_DICTIONARY,
} from './constants';
import { retrieveSession, setSession } from './sessionHelpers';

const commonHeaders = { 'Content-Type': 'application/json' };

/**
 * Makes a DELETE request to the specified url
 *
 * @param  {string} url       The URL we want to request
 *
 * @return {Promise<object>}  The response data
 */
export function del(url) {
  return request(url, {
    headers: commonHeaders,
    method: 'DELETE',
  });
}

/**
 * Makes a GET request to the specified url
 *
 * @param  {string} url The URL we want to request
 * @param  {object} [headersObject] Optional extra headers
 * @param  {object} [nonFetchOptions] Optional extra arguments to be used by the 'request'
 *                            function's logic itself, not passed to 'fetch'
 * @param  {boolean} [nonFetchOptions.sendDeviceTokens] whether to send the X-Device-Tokens header
 *
 * @return {Promise<object>} The response data
 */
export function getNoCors(url, headersObject = {}, nonFetchOptions) {
  const headersToSend = { ...commonHeaders, ...headersObject };
  return request(
    url,
    {
      headers: headersToSend,
      method: 'GET',
      mode: 'no-cors',
    },
    nonFetchOptions,
  );
}

/**
 * Makes a GET request to the specified url
 *
 * @param  {string} url The URL we want to request
 * @param  {object} [headersObject] Optional extra headers
 * @param  {object} [nonFetchOptions] Optional extra arguments to be used by the 'request'
 *                            function's logic itself, not passed to 'fetch'
 * @param  {boolean} [nonFetchOptions.sendDeviceTokens] whether to send the X-Device-Tokens header
 * @param  {object} [options] Optional miscellaneous arguments to be used by this 'get'
 *                            function's logic itself, not passed to 'fetch' or 'request'
 * @param  {boolean} [options.excludeCommonHeaders] whether to exclude commonHeaders (Default=false)
 *
 * @return {Promise<object>} The response data
 */
export function get(url, headersObject = {}, nonFetchOptions, options = {}) {
  const { excludeCommonHeaders = false } = options || {};
  const commonHeadersToUse = excludeCommonHeaders ? {} : commonHeaders;
  const headersToSend = { ...commonHeadersToUse, ...headersObject };
  return request(
    url,
    {
      headers: headersToSend,
      method: 'GET',
    },
    nonFetchOptions,
  );
}

/**
 * Makes a POST request to the specified url
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} payload   The data to set as the POST body
 * @param  {object} [nonFetchOptions] Extra arguments to be used by the 'request' function's logic
 *                            itself, not passed to 'fetch'
 * @param  {boolean} [nonFetchOptions.sendDeviceTokens] whether to send the X-Device-Tokens header
 *
 * @return {Promise<object>}  The response data
 */
export function post(url, payload, nonFetchOptions) {
  return request(
    url,
    {
      body: JSON.stringify(payload),
      headers: commonHeaders,
      method: 'POST',
    },
    nonFetchOptions,
  );
}

/**
 * Makes a PUT request to the specified url
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} payload   The data to set as the PUT body
 * @param  {object} [nonFetchOptions] Extra arguments to be used by the 'request' function's logic
 *                            itself, not passed to 'fetch'
 * @param  {boolean} [nonFetchOptions.sendDeviceTokens] whether to send the X-Device-Tokens header
 * @return {Promise<object>}  The response data
 */
export function putRequest(url, payload, nonFetchOptions) {
  return request(
    url,
    {
      body: JSON.stringify(payload),
      headers: commonHeaders,
      method: 'PUT',
    },
    nonFetchOptions,
  );
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch" (with headers still
 *                            as plain object)
 * @param  {object} [options.headers] plain JS object of headers to pass to 'fetch'
 * @param  {object} [nonFetchOptions] Extra arguments to be used by request logic itself, not passed
 *                            to fetch
 * @param  {boolean} [nonFetchOptions.sendDeviceTokens] whether to send the X-Device-Tokens header
 *
 * @return {Promise<object>}  The response data
 */
function* request(url, fetchOptions = {}, nonFetchOptions = {}) {
  const {
    includeCredentials = true,
    sendAnonymousSplitId,
    sendDeviceTokens,
    withSession = true,
  } = nonFetchOptions;
  const options = {
    ...fetchOptions,
    headers: { ...fetchOptions.headers },
  };
  if (withSession) {
    const session = yield call(retrieveSession);
    const sessionHeaders = yield call(
      createHeadersFromSessionData,
      session,
      sendDeviceTokens,
      sendAnonymousSplitId,
    );
    options.headers = { ...options.headers, ...sessionHeaders };
    options.credentials = includeCredentials ? 'include' : 'same-origin';
  }
  const response = yield call(fetch, url, options);
  if (withSession) {
    yield call(updateStorageFromHeaders, response.headers);
  }
  yield call(checkStatus, response);
  const parsedResponse = yield call(parseResponse, response);
  return parsedResponse;
}

/**
 * Updates session attributes (authorization token, selected loan) and
 * non-session attributes (device token) with any response custom headers.
 * - If the header is not sent (ie, it's value is undefined), we do not update it's associated
 * entry.
 * - If the header value is an empty string, we remove the associated entry.
 * - Else we replace the associated entry with the newer header value.
 *
 * @param {object} headers The response object headers sent back from a network request
 *
 * @returns {void}
 */
function* updateStorageFromHeaders(headers) {
  const updatedSessionProperties = yield call(getSessionDataFromHeaders, headers);
  if (!isEmpty(updatedSessionProperties)) {
    const currentSession = yield call(retrieveSession);
    const newSession = { ...currentSession, ...updatedSessionProperties };
    yield call(setSession, newSession);
  }

  // Set non-session-related custom headers to AsyncStorage
  try {
    const deviceTokens = headers.get(DEVICE_TOKENS_HEADER_TITLE);
    if (deviceTokens) {
      yield call(AsyncStorage.setItem, DEVICE_TOKENS_STORAGE_KEY, deviceTokens);
    }
  } catch {
    try {
      yield call(AsyncStorage.removeItem, DEVICE_TOKENS_STORAGE_KEY);
    } catch {
      // Rare, obscure, maybe impossible error case. Do nothing for now.
    }
  }

  try {
    const anonymousId = headers.get(ANONYMOUS_SPLIT_ID_HEADER_TITLE);
    if (anonymousId) {
      yield call(AsyncStorage.setItem, ANONYMOUS_SPLIT_ID_STORAGE_KEY, anonymousId);
    }
  } catch {
    try {
      yield call(AsyncStorage.removeItem, ANONYMOUS_SPLIT_ID_STORAGE_KEY);
    } catch {
      // Rare, obscure, maybe impossible error case. Do nothing for now.
    }
  }
}

/**
 * Depending on the content-type of data passed back in the network response,
 * parses the response body appropriately and returns that value.
 * @param response The response object
 * @returns {Object | String} Parsed data (object if file data or JSON, string if text)
 */
async function parseResponse(response) {
  const responseContentType = response.headers.get('Content-Type');
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  if (response.headers.get('Content-Type').match('application/csv')) {
    // grabs filename from the headers and then resolves the blob promise
    // before triggering download
    const contentDisposition = response.headers.get('Content-Disposition');
    const fileName =
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition)[1] || 'report';

    return response.blob().then((data) => ({ blob: data, fileName }));
  }
  if (CONTENT_TYPES.includes(responseContentType)) {
    const contentDisposition = response.headers.get('Content-Disposition');
    const fileName =
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition)[1] || 'file.pdf';
    return response.blob().then((blob) => ({ blob, fileName }));
  }
  if (response.status === 308) {
    return response.text();
  }
  return response.json();
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {Promise<object>} Returns either the response, or throws an error
 */
function* checkStatus(response) {
  const { status } = response;
  if ((status >= 200 && status < 300) || status === 308) {
    return response;
  }
  if (status === 401) {
    yield put(logoutRequest({ isSessionExpired: true }));
  }
  if (status >= 400) {
    // await for response to parse before extracting error message
    if (response.headers.get('Content-Type').match('text')) {
      throw yield response.text();
    }
    throw yield response.json();
  }

  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

/**
 * Given a fetch's response.headers object, returns an object with any
 * custom session-related header values
 *
 * @param {object} responseHeaders fetch API's response.headers object (a Headers instance)
 * @returns {object} object of entries to be stored
 */
const getSessionDataFromHeaders = (responseHeaders) => (
  Object
    .entries(SESSION_HEADERS_STORAGE_DICTIONARY)
    .reduce((acc, [sessionStorageKey, { responseHeaderTitle }]) => {
      const value = responseHeaders.get(responseHeaderTitle);
      if (value === null) { // null value means header is not present on the Headers instance
        return acc;
      }
      return { ...acc, [sessionStorageKey]: value };
    }, {})
);

/**
 * Generates an object to be used as request headers, derived from the current session attributes
 *
 * @param {Object} session The current session values
 * @param {boolean} sendDeviceTokens Whether to include the deviceTokens storage data in the return
 *                                   value to send off as a header
 * @param {boolean} sendAnonymousSplitId Whether to include the anonymous Split.io ID in the return
 *                                       value to send off as a header
 * @returns {Promise<object>}
 */
async function createHeadersFromSessionData(session, sendDeviceTokens, sendAnonymousSplitId) {
  let sessionHeadersObject = Object.entries(session)
    // Replace storage key with its corresponding request header title; format values as necessary
    .reduce((acc, [key, rawValue]) => {
      let value = rawValue;
      if (key === AUTH_TOKEN_SESSION_KEY) {
        value = `Bearer ${rawValue}`;
      }
      const { requestHeaderTitle } = SESSION_HEADERS_STORAGE_DICTIONARY[key];
      return { ...acc, [requestHeaderTitle]: value };
    }, {});

  // Send deviceTokens header (plus session headers) if requested and if exists already in storage
  if (sendDeviceTokens) {
    let deviceTokens;
    try {
      deviceTokens = await AsyncStorage.getItem(DEVICE_TOKENS_STORAGE_KEY);
    } catch {
      // Do nothing.
    }
    if (deviceTokens) {
      sessionHeadersObject = {
        ...sessionHeadersObject,
        [DEVICE_TOKENS_HEADER_TITLE]: deviceTokens,
      };
    }
  }

  // Send anonymousSplitId header (plus session headers) if requested
  if (sendAnonymousSplitId) {
    let anonymousSplitId;
    try {
      anonymousSplitId = await AsyncStorage.getItem(ANONYMOUS_SPLIT_ID_STORAGE_KEY);
    } catch {
      // Do nothing.
    }
    if (anonymousSplitId) {
      sessionHeadersObject = {
        ...sessionHeadersObject,
        [ANONYMOUS_SPLIT_ID_HEADER_TITLE]: anonymousSplitId,
      };
    }
  }

  // Else just send session headers
  return sessionHeadersObject;
}
